'use client';

import type { FC, MouseEvent } from 'react';
import { useCallback } from 'react';
import Link from 'next/link';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import tabTranslations from '../TabBar.translations';
import gameTranslations from '../../screens/GamesScreen/GamesScreen.translations';
import { createTranslations } from '../../i18n/types';

const navTranslations = createTranslations({
  en: { bonuses: 'Bonuses' },
  ru: { bonuses: 'Бонусы' },
  uz: { bonuses: 'Bonuslar' },
  fa: { bonuses: 'بونوس‌ها' },
});

import { useRushMidConfig } from '../../queries/useRushMidConfig';
import { useProfileData } from '../../queries/useProfileQueries';
import { useNavigation } from '../../contexts/NavigationContext';
import { useScrollManager } from '../../contexts/ScrollManagerContext';
import { TAB_URL_MAP, buildScreenUrl } from '../../lib/screen-url-map';
import {
  FaGift, GiOpenChest, FaClipboardList, FaUsers,
  GiCrossedSwords, FaAtom, FaArrowUp, FaStore, FaCrosshairs, FaSkull,
} from '../icons';
import type { IconType } from '../icons';
import type { TabId } from '../../types/tabs';
import type { ScreenName } from '../../types/navigation';
import { cn } from '../../utils/cn';

interface NavItem {
  type: 'tab' | 'game';
  id: string;
  tabId: TabId;
  screen?: ScreenName;
  icon: IconType;
  labelKey: string;
  translationSource: 'tab' | 'game' | 'nav';
  /** Neon accent color for active state */
  neon?: string;
}

const navItems: NavItem[] = [
  { type: 'tab', id: 'balance', tabId: 'balance', icon: FaGift, labelKey: 'bonuses', translationSource: 'nav', neon: '#f472b6' },
  { type: 'tab', id: 'cases', tabId: 'cases', icon: GiOpenChest, labelKey: 'cases', translationSource: 'tab', neon: '#f59e0b' },
  { type: 'game', id: 'rush-mid', tabId: 'games', screen: 'rush-mid' as ScreenName, icon: FaCrosshairs, labelKey: 'rushMidTitle', translationSource: 'game', neon: '#f43f5e' },
  { type: 'game', id: 'roulette', tabId: 'games', screen: 'roulette' as ScreenName, icon: FaSkull, labelKey: 'rouletteTitle', translationSource: 'game', neon: '#dc2626' },
  { type: 'game', id: 'case-battles', tabId: 'games', screen: 'case-battles' as ScreenName, icon: GiCrossedSwords, labelKey: 'caseBattlesTitle', translationSource: 'game', neon: '#ef4444' },
  { type: 'game', id: 'contracts', tabId: 'games', screen: 'contracts' as ScreenName, icon: FaAtom, labelKey: 'contractsTitle', translationSource: 'game', neon: '#a78bfa' },
  { type: 'game', id: 'upgrade', tabId: 'games', screen: 'upgrade' as ScreenName, icon: FaArrowUp, labelKey: 'upgradeTitle', translationSource: 'game', neon: '#34d399' },
  { type: 'game', id: 'shop', tabId: 'games', screen: 'shop' as ScreenName, icon: FaStore, labelKey: 'shopTitle', translationSource: 'game', neon: '#60a5fa' },
  { type: 'tab', id: 'inventory', tabId: 'inventory', icon: FaClipboardList, labelKey: 'inventory', translationSource: 'tab', neon: '#38bdf8' },
  { type: 'tab', id: 'referrals', tabId: 'referrals', icon: FaUsers, labelKey: 'referrals', translationSource: 'tab', neon: '#4ade80' },
];

export const DesktopNav: FC = () => {
  const tTab = useLocalTranslation(tabTranslations);
  const tGame = useLocalTranslation(gameTranslations);
  const tNav = useLocalTranslation(navTranslations);
  const navigation = useNavigation();
  const { clearAll: clearAllScroll } = useScrollManager();
  // while Rush Mid is teased («Скоро»), the desktop menu simply doesn't list it; same for Roulette
  // when it's restricted to skin-exchange users and this user isn't one (default restricted).
  const rushMid = useRushMidConfig();
  const profile = useProfileData();
  const rouletteLocked = (profile?.rouletteRestrictedMode ?? true) && !(profile?.skinsExchangeEnabled ?? false);
  const items = navItems.filter(
    (it) => (it.id !== 'rush-mid' || !rushMid.comingSoon) && (it.id !== 'roulette' || !rouletteLocked),
  );

  const activeTab = navigation.state.activeTab;
  const currentStack = navigation.state.stacks[activeTab];
  const stackScreens = currentStack?.map((s) => s.screen) ?? [];

  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>, item: NavItem) => {
      // Allow Cmd/Ctrl/middle-click to open in a new tab via the
      // browser's default Link behaviour.
      if (
        event.metaKey || event.ctrlKey || event.shiftKey ||
        event.altKey || event.button !== 0
      ) {
        return;
      }
      event.preventDefault();
      clearAllScroll();
      if (item.type === 'tab') {
        navigation.changeTab(item.tabId);
        navigation.resetStack(item.tabId, item.tabId as ScreenName);
      } else if (item.screen) {
        navigation.changeTab('games');
        navigation.resetStack('games', 'games' as ScreenName);
        navigation.pushScreen('games', item.screen);
      }
    },
    [navigation, clearAllScroll],
  );

  const itemHref = (item: NavItem): string => {
    if (item.type === 'tab') return TAB_URL_MAP[item.tabId];
    return buildScreenUrl(item.screen!);
  };

  const isActive = (item: NavItem) => {
    if (item.type === 'tab') {
      return activeTab === item.tabId;
    }
    // Game subitem: active when (a) we're in the games stack with this
    // screen on top, or (b) we're on a standalone-tab route whose tab
    // id collides with the screen name (e.g. `/shop` maps to tab `shop`,
    // not `games`, in TAB_BY_PREFIX).
    if (activeTab === 'games' && stackScreens.includes(item.screen!)) return true;
    return (activeTab as string) === (item.screen as string);
  };

  return (
    <nav className="relative border-b border-white/[0.04] px-6 h-12 flex items-center justify-center gap-0.5 shrink-0 overflow-x-auto [scrollbar-width:none]">
      {items.map((item) => {
        const active = isActive(item);
        const label =
          item.translationSource === 'nav'
            ? tNav.t(item.labelKey as any)
            : item.translationSource === 'tab'
              ? tTab.t(item.labelKey as any)
              : tGame.t(item.labelKey as any);

        const neon = item.neon || '#fff';

        return (
          <Link
            key={item.id}
            href={itemHref(item)}
            prefetch
            className={cn(
              'relative flex items-center gap-1.5 px-3.5 py-1.5 border-none cursor-pointer text-xs uppercase tracking-wider whitespace-nowrap bg-transparent outline-none transition-all duration-200 font-medium no-underline',
              active
                ? 'text-white'
                : 'text-white/35 hover:text-white/60',
            )}
            style={active ? {
              textShadow: `0 0 8px ${neon}80, 0 0 16px ${neon}40`,
            } : undefined}
            onClick={(e) => handleClick(e, item)}
          >
            <span>{label}</span>

            {/* Neon underline glow */}
            {active && (
              <span
                className="absolute bottom-0 start-2 end-2 h-[2px] rounded-full"
                style={{
                  background: neon,
                  boxShadow: `0 0 6px ${neon}, 0 0 12px ${neon}80, 0 2px 8px ${neon}60`,
                }}
              />
            )}
          </Link>
        );
      })}
    </nav>
  );
};
